import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SubscriptionDetails } from './SubscriptionDetails';
import { SubscriptionForm } from './SubscriptionForm';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Edit, Plus, Info, Settings } from 'lucide-react';

interface SubscriptionManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  userCount: number;
}

export function SubscriptionManagementDialog({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  userCount,
}: SubscriptionManagementDialogProps) {
  const [showForm, setShowForm] = useState(false);
  const { fetchSubscription, fetchSubscriptionCost, createSubscription, updateSubscription } = useSubscriptions();
  
  const { data: subscription, isLoading: loadingSubscription } = fetchSubscription(tenantId);
  const { data: cost, isLoading: loadingCost } = fetchSubscriptionCost(tenantId);

  const handleSubmit = async (data: any) => {
    if (subscription) {
      await updateSubscription.mutateAsync(data);
    } else {
      await createSubscription.mutateAsync({ ...data, tenant_id: tenantId });
    }
    setShowForm(false);
  };

  const isLoading = loadingSubscription || loadingCost;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subscription Management - {tenantName}</DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : !subscription ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Subscription Configured</h3>
              <p className="text-sm text-muted-foreground mb-6">
                This tenant doesn't have a subscription plan yet.
              </p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Subscription
              </Button>
            </div>
          ) : (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-auto p-1 bg-muted/50">
                <TabsTrigger value="details" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                  <Info className="h-4 w-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                  <Settings className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4">
                {cost && <SubscriptionDetails subscription={subscription} cost={cost} />}
                <div className="flex justify-end">
                  <Button onClick={() => setShowForm(true)} variant="outline">
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Subscription
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="settings">
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Manage subscription settings and preferences.
                  </p>
                  <Button onClick={() => setShowForm(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Subscription
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <SubscriptionForm
        open={showForm}
        onOpenChange={setShowForm}
        subscription={subscription}
        onSubmit={handleSubmit}
        isSubmitting={createSubscription.isPending || updateSubscription.isPending}
        userCount={userCount}
      />
    </>
  );
}
